import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  LineChart,
  Radar,
  Wallet,
  History,
  TrendingUp,
  Receipt,
  Activity,
  Cpu,
  Brain,
  ClipboardList,
  Settings as SettingsIcon,
  Lock,
  FileSpreadsheet,
  GitCompareArrows,
  PanelLeftClose,
  PanelLeft,
  Menu,
  Ghost,
  Target,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { useSidebarMode } from "@/lib/sidebarMode";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/markets", label: "Markets", icon: LineChart, testid: "nav-markets" },
  { to: "/signals", label: "Signals", icon: Radar, testid: "nav-signals" },
  { to: "/screener", label: "Screener", icon: Target, testid: "nav-screener" },
  { to: "/positions", label: "Paper Positions", icon: Wallet, testid: "nav-positions" },
  { to: "/trades", label: "Trade History", icon: History, testid: "nav-trades" },
  { to: "/performance", label: "Performance", icon: TrendingUp, testid: "nav-performance" },
  { to: "/costs", label: "Costs", icon: Receipt, testid: "nav-costs" },
  { to: "/candle-logs", label: "Candle Logs", icon: FileSpreadsheet, testid: "nav-candle-logs" },
  { to: "/data-health", label: "Data Health", icon: Activity, testid: "nav-data-health" },
  { to: "/algorithm", label: "Algorithm", icon: Cpu, testid: "nav-algorithm" },
  { to: "/mtf-lab", label: "MTF Lab", icon: Sparkles, testid: "nav-mtf-lab" },
  { to: "/comparison", label: "Comparison", icon: GitCompareArrows, testid: "nav-comparison" },
  { to: "/chatgpt-export", label: "ChatGPT Export", icon: MessageSquare, testid: "nav-chatgpt-export" },
  { to: "/shadow-rejected", label: "Shadow P&L", icon: Ghost, testid: "nav-shadow-rejected" },
  { to: "/ml", label: "ML Model", icon: Brain, testid: "nav-ml" },
  { to: "/audit", label: "Audit Log", icon: ClipboardList, testid: "nav-audit" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/live", label: "Live Trading", icon: Lock, testid: "nav-live", locked: true },
];

export default function Sidebar() {
  const { mode, cycle, isMobile } = useSidebarMode();
  const isIcon = mode === "icon" && !isMobile;
  const isHidden = mode === "hidden";

  // Floating hamburger appears when sidebar is fully hidden.
  if (isHidden) {
    return (
      <button
        data-testid="sidebar-open-btn"
        onClick={cycle}
        title="Show sidebar"
        className="fixed left-3 z-40 h-9 w-9 rounded-sm border border-slate-700 bg-[#0d1421] flex items-center justify-center text-slate-300 hover:text-white hover:border-blue-500 sidebar-open-safe"
      >
        <Menu size={16} />
      </button>
    );
  }

  return (
    <>
      {/* Backdrop overlay on mobile so tapping outside closes the drawer */}
      {isMobile && (
        <button
          data-testid="sidebar-backdrop"
          onClick={cycle}
          aria-label="Close sidebar"
          className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-screen border-r border-slate-800 bg-[#0d1421] z-30 flex flex-col transition-[width] duration-150 ${
          isMobile ? "w-64" : isIcon ? "w-16" : "w-60"
        }`}
        data-testid="sidebar"
        data-sidebar-mode={mode}
        data-mobile={isMobile ? "true" : "false"}
      >
      <div className={`h-14 flex items-center border-b border-slate-800 ${isIcon ? "justify-center px-0" : "px-4"}`}>
        {!isIcon && (
          <>
            <span className="text-lg font-semibold tracking-tight">
              MyEdge<span className="text-blue-400">.</span>
            </span>
            <span className="ml-auto text-[9px] uppercase tracking-[0.2em] text-slate-500">v1</span>
          </>
        )}
        <button
          data-testid="sidebar-toggle-btn"
          onClick={cycle}
          title={isIcon ? "Expand sidebar" : "Collapse sidebar"}
          className={`h-7 w-7 flex items-center justify-center rounded-sm text-slate-400 hover:text-white hover:bg-slate-800 ${
            isIcon ? "" : "ml-2"
          }`}
        >
          {isIcon ? <PanelLeft size={14} /> : <PanelLeftClose size={14} />}
        </button>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
        {NAV.map(({ to, label, icon: Icon, testid, locked }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            title={isIcon ? label : undefined}
            onClick={() => { if (isMobile) cycle(); }}
            className={({ isActive }) =>
              `flex items-center gap-3 py-2 text-sm border-l-2 transition-colors ${
                isIcon ? "px-0 justify-center" : "px-4"
              } ${
                isActive
                  ? "border-blue-500 text-white bg-slate-800/40"
                  : "border-transparent text-slate-400 hover:text-white hover:bg-slate-800/30"
              } ${locked ? "opacity-60" : ""}`
            }
          >
            <Icon size={16} className="shrink-0" />
            {!isIcon && <span className="flex-1 truncate">{label}</span>}
            {!isIcon && locked && <Lock size={12} className="text-slate-500" />}
          </NavLink>
        ))}
      </nav>

      {!isIcon && (
        <div className="p-4 border-t border-slate-800 text-[10px] uppercase tracking-[0.15em] text-slate-500 leading-relaxed">
          Paper mode only.
          <br />
          Live trading disabled.
        </div>
      )}
      </aside>
    </>
  );
}
